import { Link } from "@tanstack/react-router";
import { Clock, ArrowUpRight } from "lucide-react";
import type { BlogPost } from "@/data/blog";
import { CATEGORIES } from "@/data/blog";

type Variant = "default" | "featured" | "compact" | "hero";

export function BlogCard({ post, variant = "default" }: { post: BlogPost; variant?: Variant }) {
  const category = CATEGORIES.find((c) => c.slug === post.category);
  const isHero = variant === "hero";
  const isFeatured = variant === "featured";
  const isCompact = variant === "compact";

  return (
    <Link
      to="/blog/$slug"
      params={{ slug: post.slug }}
      className={`group relative flex overflow-hidden rounded-3xl border border-border/60 bg-card/40 backdrop-blur transition-all duration-500 hover:-translate-y-1 hover:border-primary/60 hover:shadow-[0_30px_80px_-30px] hover:shadow-primary/40 ${
        isHero ? "flex-col lg:flex-row" : isFeatured ? "flex-col md:flex-row" : "flex-col"
      }`}
    >
      <div
        className={`relative overflow-hidden ${
          isHero ? "aspect-[16/10] lg:aspect-auto lg:w-3/5" : isFeatured ? "aspect-[16/10] md:aspect-auto md:w-1/2" : isCompact ? "aspect-[16/9]" : "aspect-[16/10]"
        }`}
        style={{ background: post.coverGradient }}
      >
        <img
          src={post.coverImage}
          alt={post.title}
          loading="lazy"
          decoding="async"
          className="absolute inset-0 h-full w-full object-cover opacity-95 transition-transform duration-700 group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
        {category && (
          <span className="absolute left-4 top-4 rounded-full bg-black/50 px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-white backdrop-blur-md ring-1 ring-white/10">
            {category.name}
          </span>
        )}
        {post.city && (
          <span className="absolute right-4 top-4 rounded-full bg-primary/90 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-primary-foreground backdrop-blur-md">
            {post.city}
          </span>
        )}
        {post.sector && !post.city && (
          <span className="absolute right-4 top-4 rounded-full bg-white/15 px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-white backdrop-blur-md ring-1 ring-white/20">
            {post.sector}
          </span>
        )}
        {isHero && (
          <div className="absolute bottom-5 left-5 right-5 hidden text-white lg:block">
            <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-white/80">Articolo in evidenza</span>
          </div>
        )}
      </div>
      <div
        className={`flex flex-1 flex-col gap-4 ${
          isHero ? "p-8 lg:p-12 lg:justify-center" : isFeatured ? "p-8 md:p-10 md:justify-center" : "p-6"
        }`}
      >
        <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
          <time dateTime={post.publishedAt}>
            {new Date(post.publishedAt).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" })}
          </time>
          <span aria-hidden>·</span>
          <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {post.readingMinutes} min</span>
        </div>
        <h3
          className={`font-bold leading-tight tracking-tight text-foreground transition-colors group-hover:text-primary ${
            isHero ? "text-3xl lg:text-4xl xl:text-5xl" : isFeatured ? "text-2xl md:text-3xl" : isCompact ? "text-base" : "text-lg"
          }`}
        >
          {post.title}
        </h3>
        {!isCompact && (
          <p className={`text-sm text-muted-foreground ${isHero ? "lg:text-base xl:text-lg" : isFeatured ? "md:text-base" : "line-clamp-2"}`}>
            {post.excerpt}
          </p>
        )}
        <div className="mt-auto flex items-center justify-between pt-3 text-xs text-muted-foreground">
          <span className="flex items-center gap-2">
            <span className="grid h-7 w-7 place-items-center rounded-full bg-primary/15 text-[10px] font-bold text-primary">
              {post.author.name.split(" ").map((w) => w[0]).join("").slice(0, 2)}
            </span>
            <span>{post.author.name}</span>
          </span>
          <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-primary" />
        </div>
      </div>
    </Link>
  );
}
